import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";

const PING_MS = 25000;
const MAX_BACKOFF_MS = 15000;

// Lawyer-level event socket (/api/ws/lawyer). Reconnects indefinitely with
// backoff while mounted; `onEvent` receives every server frame, `onReconnect`
// fires on each (re)open so callers can catch up on anything missed.
export function useLawyerSocket({ enabled, onEvent, onReconnect }) {
  const [connected, setConnected] = useState(false);
  const attemptRef = useRef(0);
  const onEventRef = useRef(onEvent);
  const onReconnectRef = useRef(onReconnect);
  onEventRef.current = onEvent;
  onReconnectRef.current = onReconnect;

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    let sock = null;
    let retryId = null;
    let pingId = null;

    const scheduleRetry = () => {
      if (!alive) return;
      const attempt = ++attemptRef.current;
      retryId = setTimeout(connect, Math.min(1000 * Math.pow(2, attempt - 1), MAX_BACKOFF_MS));
    };

    const connect = async () => {
      if (!alive) return;
      try {
        const r = await api.post("/lawyer/ws-ticket");
        const ticket = r.data?.ticket;
        if (!ticket || !alive) return;
        const ws = new WebSocket(`${API.replace(/^http/, "ws")}/ws/lawyer?token=${ticket}`);
        sock = ws;
        ws.onopen = () => {
          const wasRetry = attemptRef.current > 0;
          attemptRef.current = 0;
          setConnected(true);
          if (wasRetry) onReconnectRef.current?.();
          clearInterval(pingId);
          pingId = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
          }, PING_MS);
        };
        ws.onmessage = (m) => {
          try { onEventRef.current?.(JSON.parse(m.data)); } catch (error) { console.warn("[lawyer-ws] bad frame or handler error", error?.message || error); }
        };
        ws.onclose = () => {
          setConnected(false);
          clearInterval(pingId);
          scheduleRetry();
        };
        ws.onerror = () => { console.warn("[lawyer-ws] socket error"); try { ws.close(); } catch { /* already closed */ } };
      } catch {
        scheduleRetry();
      }
    };

    connect();
    return () => {
      alive = false;
      clearTimeout(retryId);
      clearInterval(pingId);
      try { sock?.close(); } catch { /* ignore */ }
    };
  }, [enabled]);

  return { connected };
}
